import { Card, CardContent } from '@/app/common/components/Card'
import { InfoCircleIcon } from '@/app/common/icons/general'
import { currencyFormatter } from '@/app/common/utilities'
import { PurseHistory } from '@/schemas/payments.schema'
import { useTranslation } from 'react-i18next'

type Props = {
  purseMovements: PurseHistory[]
  allocatedTotal: number
  /** The amount the allocation has to add up to for the API to accept it. */
  requiredTotal: number
}

/**
 * Explains that the wallets below are not a fresh pick but the reservation the
 * payment already carries, so the applicant understands why money is already
 * spoken for and what happens if they change it.
 */
export function ExistingAllocationNotice({
  purseMovements,
  allocatedTotal,
  requiredTotal,
}: Props) {
  const { t } = useTranslation()

  if (purseMovements.length === 0) {
    return null
  }

  const matchesRequiredTotal = allocatedTotal === requiredTotal

  return (
    <Card>
      <CardContent>
        <div className="flex items-start">
          <InfoCircleIcon className="text-blue-500 text-2xl mr-2 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-gray-800">
              {t('existing_allocation.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {matchesRequiredTotal
                ? t('existing_allocation.description', {
                    amount: currencyFormatter.format(allocatedTotal),
                  })
                : t('existing_allocation.total_mismatch', {
                    amount: currencyFormatter.format(allocatedTotal),
                    required: currencyFormatter.format(requiredTotal),
                  })}
            </p>
            <ul className="mt-2 space-y-1 text-sm text-gray-700">
              {purseMovements.map((movement) => (
                <li className="flex justify-between gap-4" key={movement.id}>
                  <span>
                    {movement.name}
                    <span className="text-gray-500">
                      {' '}
                      · {movement.student_name}
                    </span>
                  </span>
                  <span className="font-medium">
                    {currencyFormatter.format(movement.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
